import { ArrowRight, CalendarClock, CheckCircle2, Clock3, Loader2, Pin, Trophy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameContinueStatus } from 'shared/enums/game/game-continue-status.enum';
import { JourneyDto, JourneyTrackingState, JourneyType } from 'shared/interfaces/engagement/journey/journey.interface';
import { dealApi } from 'src/api/deal/deal';
import { journeysApi } from 'src/api/engagement/journeys';
import { gameApi } from 'src/api/game/game';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { useVerisoul } from 'src/hooks/use-verisoul';
import { openEntryLink } from 'src/utils/entry-link.util';

const stateCopy: Record<JourneyTrackingState, string> = {
  [JourneyTrackingState.Started]: 'Session started',
  [JourneyTrackingState.TrackingPending]: 'Waiting for a partner tracking update',
  [JourneyTrackingState.TrackingConfirmed]: 'Partner tracking confirmed',
  [JourneyTrackingState.GoalPending]: 'Goal reported; credit is pending',
  [JourneyTrackingState.Credited]: 'Reward credited',
  [JourneyTrackingState.Expired]: 'Expired',
  [JourneyTrackingState.UnsupportedResume]: 'Resume is not supported by this partner',
  [JourneyTrackingState.Archived]: 'In history',
};

const formatReward = (points: number, xp: number, formatPoints: (points: number) => string): string => {
  const values = [];
  if (points > 0) values.push(formatPoints(points));
  if (xp > 0) values.push(`${xp.toLocaleString()} XP`);
  return values.join(' + ') || 'No confirmed reward yet';
};

const PinnedJourneyCard = () => {
  const { formatPoints } = useUserCurrency();
  const navigate = useNavigate();
  const { getSessionId } = useVerisoul();
  const [journey, setJourney] = useState<JourneyDto | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await journeysApi.list('active');
      const selected =
        response.journeys.find((candidate) => candidate.id === response.pinnedJourneyId) ??
        response.journeys[0] ??
        null;
      setJourney(selected);
      setIsPinned(selected?.isPinned === true);
      setError(null);
    } catch {
      setJourney(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const expiryCopy = useMemo(() => {
    if (!journey?.expiresOn) return 'No known expiry';
    return `Expires ${new Date(journey.expiresOn).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })}`;
  }, [journey?.expiresOn]);

  if (isLoading) {
    return <div className="h-44 animate-pulse rounded-3xl border border-violet-100 bg-violet-50/60" />;
  }
  if (!journey) return null;

  const pinJourney = async () => {
    setIsWorking(true);
    try {
      const updated = await journeysApi.pin(journey.id);
      setJourney(updated);
      setIsPinned(true);
    } finally {
      setIsWorking(false);
    }
  };

  const resumeJourney = async () => {
    if (!journey.canResume || !journey.sessionUid) {
      navigate(`/journeys/${journey.id}`);
      return;
    }
    setIsWorking(true);
    setError(null);
    try {
      if (journey.type === JourneyType.Game) {
        const sessionId = await getSessionId();
        const response = await gameApi.continueGame(journey.sessionUid, sessionId ?? undefined);
        if (response.gameContinueStatus === GameContinueStatus.Success && response.entryLink) {
          openEntryLink(response.entryLink);
          return;
        }
        setError('The partner could not reopen this game right now.');
      } else {
        const response = await dealApi.continueDeal(journey.sessionUid);
        if (response.entryLink) {
          openEntryLink(response.entryLink);
          return;
        }
        setError('The partner could not reopen this deal right now.');
      }
    } catch {
      setError('The partner could not reopen this journey right now.');
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-3xl border border-violet-200 bg-gradient-to-br from-white via-violet-50/70 to-blue-50 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-violet-100 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-violet-700">
          <Pin className="h-4 w-4" />
          {isPinned ? 'Pinned journey' : 'Journey'}
        </div>
        <button
          type="button"
          onClick={() => navigate('/journeys')}
          className="text-xs font-bold text-violet-700 hover:text-violet-900"
        >
          View all
        </button>
      </div>
      <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-violet-100">
            {journey.imageUrl ? (
              <img src={journey.imageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <Trophy className="h-7 w-7 text-violet-500" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-manrope text-lg font-extrabold text-gray-950">{journey.title}</p>
            <p className="mt-0.5 text-xs font-semibold text-gray-500">
              Started {new Date(journey.startedOn).toLocaleDateString()} · {journey.partnerName}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
              <span className="inline-flex items-center gap-1">
                <Clock3 className="h-3.5 w-3.5 text-violet-500" />
                {stateCopy[journey.trackingState]}
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3.5 w-3.5 text-violet-500" />
                {expiryCopy}
              </span>
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                {formatReward(journey.paidReward.points, journey.paidReward.xp, formatPoints)}
              </span>
            </div>
            {journey.nextGoal && (
              <p className="mt-2 text-sm text-gray-700">
                Next known goal: <span className="font-bold">{journey.nextGoal.name || 'Partner goal'}</span>
              </p>
            )}
            {error && <p className="mt-2 text-xs font-semibold text-red-700">{error}</p>}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {!isPinned && (
            <button
              type="button"
              onClick={() => void pinJourney()}
              disabled={isWorking}
              className="min-h-11 rounded-xl border border-violet-200 bg-white px-4 text-sm font-bold text-violet-800 hover:bg-violet-50 disabled:opacity-60"
            >
              Pin to Home
            </button>
          )}
          <button
            type="button"
            onClick={() => void resumeJourney()}
            disabled={isWorking}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-violet-700 px-5 text-sm font-bold text-white hover:bg-violet-800 disabled:opacity-60"
          >
            {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {journey.canResume ? 'Continue' : 'View details'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PinnedJourneyCard;
